import type { CookieOptions } from './cookies';

type AffiliateSearchParams = {
  id?: string | string[];
};

export const AFFILIATE_STORAGE_KEY = 'ilottoAffiliateData';
export const AFFILIATE_TTL_MS = 48 * 60 * 60 * 1000;
export const AFFILIATE_COOKIE_MAX_AGE = 48 * 60 * 60;

export type AffiliateStoredData = {
  soldById: string;
  capturedAt: string;
};

export const AFFILIATE_COOKIE_OPTIONS: CookieOptions = {
  path: '/',
  maxAge: AFFILIATE_COOKIE_MAX_AGE,
  sameSite: 'lax',
};

export const getAffiliateIdFromSearchParams = (
  searchParams?: AffiliateSearchParams,
): string | undefined => {
  const affiliateIdParam = searchParams?.id;

  if (Array.isArray(affiliateIdParam)) {
    return affiliateIdParam[0];
  }

  return affiliateIdParam;
};

export const cleanAffiliateId = (id: string): string => {
  if (id.includes('/?id=')) {
    return id.split('/?id=')[1];
  }
  if (id.includes('?id=')) {
    return id.split('?id=')[1];
  }
  return id;
};

export const serializeAffiliateData = (soldById: string): string => {
  const data: AffiliateStoredData = {
    soldById,
    capturedAt: new Date().toISOString(),
  };

  return JSON.stringify(data);
};

export const parseAffiliateData = (
  raw: string | AffiliateStoredData | null | undefined,
): AffiliateStoredData | null => {
  if (!raw) {
    return null;
  }

  let data: unknown;

  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  } else {
    data = raw;
  }

  if (
    typeof data !== 'object' ||
    data === null ||
    typeof (data as AffiliateStoredData).soldById !== 'string' ||
    typeof (data as AffiliateStoredData).capturedAt !== 'string'
  ) {
    return null;
  }

  const capturedAt = new Date((data as AffiliateStoredData).capturedAt);
  const diff = Date.now() - capturedAt.getTime();

  if (diff >= AFFILIATE_TTL_MS) {
    return null;
  }

  return data as AffiliateStoredData;
};

export const getAffiliateIdFromCookie = (
  cookieValue?: string | null,
): string | undefined => {
  return parseAffiliateData(cookieValue)?.soldById;
};
